import { TEMPLATES, getLayoutSlots, getSlotCount } from "@/lib/layouts";
import { X, Upload, RotateCw, Maximize2, Minimize2 } from "lucide-react";
import { useRef } from "react";

/**
 * Renders one A4 collage page.
 * props:
 *  - page: { id, templateId, imageIds: string[] }
 *  - photosPerPage: number
 *  - imagesMap: { [id]: {url} }
 *  - slotAdjustments: { [pageId]: { [slotIndex]: {zoom, rotate} } }
 *  - pageIndex, registerRef
 *  - onReplaceImage(pageIndex, slotIndex, file)
 *  - onRemoveImage(pageIndex, slotIndex)
 *  - onAdjust(pageIndex, slotIndex, patch)
 *  - forExport
 */
export default function CollagePage({
  page,
  photosPerPage,
  imagesMap,
  pageIndex,
  registerRef,
  onReplaceImage,
  onRemoveImage,
  onAdjust,
  slotAdjustments = {},
  forExport = false,
}) {
  const slotCount = getSlotCount(page.templateId, photosPerPage);
  const slots = getLayoutSlots(page.templateId, slotCount);
  const tmpl = TEMPLATES[page.templateId] || TEMPLATES.auto;
  const pageAdjust = slotAdjustments[page.id] || {};

  return (
    <div className="w-full">
      {!forExport && (
        <div className="flex items-baseline justify-between mb-3">
          <span
            data-testid={`page-label-${pageIndex}`}
            className="text-xs uppercase tracking-[0.25em] font-semibold text-slate-600"
          >
            Page {String(pageIndex + 1).padStart(2, "0")} · {tmpl.label}
          </span>
          <span className="text-xs text-slate-500">A4 · 210 × 297 mm</span>
        </div>
      )}
      <div
        ref={(n) => registerRef && registerRef(pageIndex, n)}
        data-testid={`collage-page-canvas-${pageIndex}`}
        className={`a4-ratio bg-white ${
          forExport ? "" : "rounded-2xl sb-shadow"
        } mx-auto w-full max-w-[720px] relative overflow-hidden`}
        style={{ boxSizing: "border-box" }}
      >
        <div className="absolute inset-[3%]">
          <div className="relative w-full h-full">
            {slots.map((s, i) => {
              const imgId = page.imageIds[i];
              const img = imgId ? imagesMap[imgId] : null;
              const adj = pageAdjust[i] || { zoom: 1, rotate: 0, fit: "cover" };
              return (
                <SlotBox
                  key={i}
                  rect={s}
                  img={img}
                  adj={adj}
                  pageIndex={pageIndex}
                  slotIndex={i}
                  onReplaceImage={onReplaceImage}
                  onRemoveImage={onRemoveImage}
                  onAdjust={onAdjust}
                  forExport={forExport}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function SlotBox({
  rect,
  img,
  adj,
  pageIndex,
  slotIndex,
  onReplaceImage,
  onRemoveImage,
  onAdjust,
  forExport,
}) {
  const inputRef = useRef(null);

  const onWheel = (e) => {
    if (!img || forExport) return;
    e.preventDefault();
    const next = Math.max(
      0.5,
      Math.min(3, (adj.zoom || 1) + (e.deltaY < 0 ? 0.06 : -0.06)),
    );
    onAdjust && onAdjust(pageIndex, slotIndex, { zoom: next });
  };

  return (
    <div
      className="absolute"
      style={{
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        padding: "3px",
      }}
    >
      <div
        className={`w-full h-full relative overflow-hidden ${
          adj.fit === "contain" ? "bg-white" : "bg-slate-100"
        } ${forExport ? "" : "rounded-md group"}`}
        onWheel={onWheel}
      >
        {img ? (
          <div
            aria-hidden
            data-fit={adj.fit === "contain" ? "contain" : "cover"}
            className="absolute inset-0 select-none"
            style={{
              backgroundImage: `url(${img.url})`,
              backgroundSize: adj.fit === "contain" ? "contain" : "cover",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              backgroundColor: adj.fit === "contain" ? "#ffffff" : undefined,
              transform: `scale(${adj.zoom || 1}) rotate(${adj.rotate || 0}deg)`,
              transformOrigin: "center center",
              transition: forExport ? "none" : "transform 0.12s ease-out",
            }}
          />
        ) : (
          !forExport && (
            <div className="w-full h-full flex items-center justify-center text-slate-400 text-[11px] uppercase tracking-[0.2em] border-2 border-dashed border-slate-200 rounded-md">
              Empty
            </div>
          )
        )}

        {!forExport && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onReplaceImage && onReplaceImage(pageIndex, slotIndex, f);
                e.target.value = "";
              }}
              data-testid={`slot-replace-input-${pageIndex}-${slotIndex}`}
            />
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                data-testid={`slot-replace-${pageIndex}-${slotIndex}`}
                className="w-8 h-8 rounded-full bg-white text-slate-900 flex items-center justify-center shadow"
                aria-label="Replace"
              >
                <Upload size={14} strokeWidth={2} />
              </button>
              {img && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      onAdjust &&
                      onAdjust(pageIndex, slotIndex, {
                        fit: adj.fit === "contain" ? "cover" : "contain",
                      })
                    }
                    data-testid={`slot-fit-${pageIndex}-${slotIndex}`}
                    title={
                      adj.fit === "contain"
                        ? "Currently: Scale to Fit — click for Scale to Fill"
                        : "Currently: Scale to Fill — click for Scale to Fit"
                    }
                    className="w-8 h-8 rounded-full bg-white text-slate-900 flex items-center justify-center shadow"
                    aria-label="Toggle fit mode"
                  >
                    {adj.fit === "contain" ? (
                      <Maximize2 size={14} strokeWidth={2} />
                    ) : (
                      <Minimize2 size={14} strokeWidth={2} />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      onAdjust &&
                      onAdjust(pageIndex, slotIndex, {
                        rotate: ((adj.rotate || 0) + 90) % 360,
                      })
                    }
                    data-testid={`slot-rotate-${pageIndex}-${slotIndex}`}
                    className="w-8 h-8 rounded-full bg-white text-slate-900 flex items-center justify-center shadow"
                    aria-label="Rotate"
                  >
                    <RotateCw size={14} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemoveImage && onRemoveImage(pageIndex, slotIndex)}
                    data-testid={`slot-remove-${pageIndex}-${slotIndex}`}
                    className="w-8 h-8 rounded-full bg-[#cb05a5] text-white flex items-center justify-center shadow"
                    aria-label="Remove"
                  >
                    <X size={14} strokeWidth={2} />
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
